/**
 * Step 3 · Materiały — the cover and the film side by side, then Opis and Materiały on the left and
 * Jak wykonać on the right. Everything here is optional, which the rail says in so many words.
 *
 * „Materiały" means the external links and nothing else („one word per thing", build spec); the
 * media block is „Okładka i film". The links editor is the entity's policy at work — `https` only,
 * three distinct destinations, a label of at most eighty — and a refusal lands on the row that
 * caused it, in the phone's own sentence (`linkRefusalKey`), rather than as a 400 after the save.
 *
 * On a DUPLICATE the block says the cover and the film do not come across: both are server-owned
 * media keyed to the source's id and no request moves them (`model/exerciseDraft`). On an EDIT the
 * cover zone shows the picture the movement already has until a new one is chosen, and the film's
 * zone says the movement has a film that a new one replaces.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  LinkBrandMark,
  MAX_EXERCISE_LINKS,
  brandKeyOf,
  linkRefusalKey,
  type ExerciseDetail,
} from '~/entities/exercise-library';
import { TrashGlyph } from '~/shared/ui/FormGlyphs';

import type { CreateExerciseState } from '../lib/useCreateExercise';
import { linkRowRefusal } from '../model/draftValidation';
import { DRAFT_LIMITS } from '../model/exerciseDraft';

import { CoverDropZone, VideoDropZone } from './DropZone';
import styles from './CreateExerciseDialog.module.css';

/**
 * Put focus on an element AFTER the render a removal causes.
 *
 * A removed row takes its bin with it, and a focused element that leaves the document drops focus
 * onto the popup — whose outline is off, and whose next Tab restarts at the rail. The target (the
 * previous row's field, or „Dodaj …" once the list is empty) may only exist after the re-render, so
 * the request is state and the effect performs it. A counter makes the same target twice two asks.
 */
function useFocusAfterRender() {
  const root = useRef<HTMLDivElement>(null);
  const [ask, setAsk] = useState<{ readonly selector: string; readonly n: number } | null>(null);
  useEffect(() => {
    if (ask !== null) root.current?.querySelector<HTMLElement>(ask.selector)?.focus();
  }, [ask]);
  return {
    root,
    focus: (selector: string) => {
      setAsk((previous) => ({ selector, n: (previous?.n ?? 0) + 1 }));
    },
  };
}

interface StepMaterialsProps {
  readonly wizard: CreateExerciseState;
  /** The movement being edited or copied — its current cover, on an edit. */
  readonly source: ExerciseDetail | null;
}

export function StepMaterials({ wizard, source }: StepMaterialsProps) {
  const { t } = useTranslation();

  return (
    <div className={`${String(styles.wide)} ${String(styles.wideFull)}`}>
      <div className={styles.block}>
        <span className={styles.label}>{t('web:exerciseLibrary.creator.media.label')}</span>
        {wizard.mode === 'duplicate' && (
          <p className={styles.hint}>{t('web:exerciseLibrary.creator.media.notCopied')}</p>
        )}
        <div className={styles.media}>
          <div className={`${String(styles.block)} ${String(styles.blockTight)}`}>
            <span className={styles.hint}>
              {t('exerciseLibrary.createWizard.mediaForm.photoTitle')}
            </span>
            <CoverDropZone
              file={wizard.draft.cover}
              currentUrl={wizard.mode === 'edit' ? (source?.coverImageUrl ?? null) : null}
              refusal={wizard.coverRefusal}
              onChoose={wizard.chooseCover}
              onRemove={wizard.removeCover}
            />
          </div>
          <div className={`${String(styles.block)} ${String(styles.blockTight)}`}>
            <span className={styles.hint}>
              {t('exerciseLibrary.createWizard.mediaForm.videoTitle')}
            </span>
            <VideoDropZone
              file={wizard.draft.video?.file ?? null}
              hasCurrent={wizard.mode === 'edit' && (source?.videoUrl ?? null) !== null}
              preparingPercent={wizard.videoPreparingPercent}
              refusal={wizard.videoRefusal}
              onChoose={wizard.chooseVideo}
              onRemove={wizard.removeVideo}
            />
          </div>
        </div>
        <p className={styles.hint}>{t('web:exerciseLibrary.creator.media.hint')}</p>
      </div>

      <div className={`${String(styles.cols)} ${String(styles.colsEven)}`}>
        <div className={styles.col}>
          <Description wizard={wizard} />
          <Links wizard={wizard} />
        </div>
        <div className={styles.col}>
          <HowTo wizard={wizard} />
        </div>
      </div>
    </div>
  );
}

function Description({ wizard }: { readonly wizard: CreateExerciseState }) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <div className={styles.block}>
      <label className={styles.label} htmlFor={id}>
        {t('web:exerciseLibrary.creator.description.label')}
      </label>
      <textarea
        id={id}
        className={`${String(styles.input)} ${String(styles.textarea)}`}
        rows={3}
        value={wizard.draft.description}
        maxLength={DRAFT_LIMITS.description}
        onChange={(event) => {
          wizard.dispatch({ type: 'setDescription', description: event.target.value });
        }}
      />
    </div>
  );
}

function HowTo({ wizard }: { readonly wizard: CreateExerciseState }) {
  const { t } = useTranslation();
  const { instructions } = wizard.draft;
  const { root: afterRoot, focus: focusAfter } = useFocusAfterRender();
  return (
    <div ref={afterRoot} className={styles.block}>
      <span className={styles.label}>{t('web:exerciseLibrary.creator.howTo.label')}</span>
      {instructions.length > 0 && (
        <div className={styles.rows}>
          {instructions.map((step, index) => {
            const n = index + 1;
            return (
              // Keyed by position: a step IS its place in the list, and a removal renumbers.
              <div key={index} className={styles.stepRow} data-row={index}>
                <span className={styles.stepNumber} aria-hidden>
                  {n}
                </span>
                <input
                  className={`${String(styles.input)} ${String(styles.rowInput)}`}
                  value={step}
                  aria-label={t('web:exerciseLibrary.creator.howTo.step', { n })}
                  placeholder={t('exerciseLibrary.createWizard.identityForm.stepPlaceholder')}
                  onChange={(event) => {
                    wizard.dispatch({ type: 'setInstruction', at: index, step: event.target.value });
                  }}
                />
                <button
                  type="button"
                  className={styles.iconButton}
                  aria-label={t('web:exerciseLibrary.creator.howTo.remove', { n })}
                  onClick={() => {
                    wizard.dispatch({ type: 'removeInstruction', at: index });
                    focusAfter(index > 0 ? `[data-row="${String(index - 1)}"] input` : '[data-add]');
                  }}
                >
                  <TrashGlyph className={styles.iconButtonGlyph} />
                </button>
              </div>
            );
          })}
        </div>
      )}
      <div className={styles.tools}>
        {instructions.length < DRAFT_LIMITS.instructions && (
          <button
            type="button"
            className={styles.textButton}
            data-add
            onClick={() => {
              wizard.dispatch({ type: 'addInstruction' });
            }}
          >
            {t('exerciseLibrary.createWizard.identityForm.addStep')}
          </button>
        )}
      </div>
    </div>
  );
}

function Links({ wizard }: { readonly wizard: CreateExerciseState }) {
  const { t } = useTranslation();
  const refusalId = useId();
  const { links } = wizard.draft;
  const refused = linkRowRefusal(links);
  const { root: afterRoot, focus: focusAfter } = useFocusAfterRender();

  return (
    <div ref={afterRoot} className={styles.block}>
      <span className={styles.label}>{t('web:exerciseLibrary.creator.links.label')}</span>
      {links.length > 0 && (
        <div className={styles.rows}>
          {links.map((link, index) => {
            const n = index + 1;
            const refusedHere = refused?.at === index;
            const host = hostOf(link.url);
            const brand = host === null ? null : brandKeyOf(host);
            return (
              // Keyed by position, as the steps above are.
              <div key={index} className={styles.block} data-row={index}>
                <div className={styles.linkRow}>
                  <span className={styles.brandPlate} aria-hidden>
                    {host !== null && <LinkBrandMark brand={brand} className={styles.brandGlyph} />}
                  </span>
                  <input
                    className={`${String(styles.input)} ${String(styles.rowInput)}`}
                    value={link.url}
                    type="url"
                    inputMode="url"
                    placeholder="https://"
                    aria-label={t('web:exerciseLibrary.creator.links.url', { n })}
                    aria-invalid={refusedHere ? 'true' : undefined}
                    data-blocker={refusedHere ? 'links' : undefined}
                    aria-describedby={refusedHere ? refusalId : undefined}
                    onChange={(event) => {
                      wizard.dispatch({ type: 'setLinkUrl', at: index, url: event.target.value });
                    }}
                  />
                  <input
                    className={`${String(styles.input)} ${String(styles.rowInput)}`}
                    value={link.label}
                    placeholder={t('web:exerciseLibrary.creator.links.labelPlaceholder')}
                    aria-label={t('web:exerciseLibrary.creator.links.labelField', { n })}
                    onChange={(event) => {
                      wizard.dispatch({
                        type: 'setLinkLabel',
                        at: index,
                        label: event.target.value,
                      });
                    }}
                  />
                  <button
                    type="button"
                    className={styles.iconButton}
                    aria-label={t('web:exerciseLibrary.creator.links.remove', { n })}
                    onClick={() => {
                      wizard.dispatch({ type: 'removeLink', at: index });
                      focusAfter(
                        index > 0 ? `[data-row="${String(index - 1)}"] input` : '[data-add]',
                      );
                    }}
                  >
                    <TrashGlyph className={styles.iconButtonGlyph} />
                  </button>
                </div>
                {refusedHere && refused !== null && (
                  <p id={refusalId} className={styles.rowRefusal}>
                    {t(linkRefusalKey(refused.refusal, link.url))}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className={styles.tools}>
        {links.length < MAX_EXERCISE_LINKS && (
          <button
            type="button"
            className={styles.textButton}
            data-add
            onClick={() => {
              wizard.dispatch({ type: 'addLink' });
            }}
          >
            {t('web:exerciseLibrary.creator.links.add')}
          </button>
        )}
        <span className={styles.hint}>{t('web:exerciseLibrary.creator.links.hint')}</span>
      </div>
    </div>
  );
}

/** The host a typed address names, for the brand plate — or `null` while it is not an address yet. */
function hostOf(url: string): string | null {
  try {
    const host = new URL(url.trim()).hostname;
    return host === '' ? null : host;
  } catch {
    return null;
  }
}
